import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/common/AppModal';
import { Alert, Button, Form, ListGroup, Spinner } from 'react-bootstrap';
import { getIndexerErrorInfo } from '../api/errors';
import { useFetchSitemapUrlsMutation } from '../api/indexerApi';
import { SitemapImportHelpTip } from './SitemapImportHelpTip';
import {
  decodeUrlForDisplay,
  formatFaNumber,
  partitionUrlsForProjectDomain,
  projectSitemapPlaceholder,
  type ProjectUrlHints,
} from '../lib/utils';

const SITEMAP_MANUAL_ENTRY_HINT =
  'در این حالت پیشنهاد می‌کنیم آدرس‌ها را دستی در «لیست آدرس‌ها» وارد کنید.';

function formatSitemapFetchError(err: unknown): string {
  const base = getIndexerErrorInfo(err).message || 'دریافت یا پارس sitemap ناموفق بود';
  if (base.includes('لیست آدرس')) return base;
  return `${base} ${SITEMAP_MANUAL_ENTRY_HINT}`;
}

function defaultSitemapUrl(project: ProjectUrlHints): string {
  return project.sitemapUrl?.trim() || projectSitemapPlaceholder(project);
}

export function SitemapImportModal({
  show,
  onClose,
  projectId,
  project,
  limit,
  existingUrls,
  onImport,
}: {
  show: boolean;
  onClose: () => void;
  projectId: string;
  project: ProjectUrlHints;
  limit?: number;
  existingUrls: string[];
  onImport: (
    urls: string[],
    options?: { lastmods?: Record<string, string> },
  ) => void;
}) {
  const [sitemapUrl, setSitemapUrl] = useState(() => defaultSitemapUrl(project));
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [fetchedUrls, setFetchedUrls] = useState<string[]>([]);
  const [fetchedLastmods, setFetchedLastmods] = useState<Record<string, string>>(
    {},
  );
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [filter, setFilter] = useState('');
  const [fetchSitemapUrls] = useFetchSitemapUrlsMutation();

  const existingSet = useMemo(() => new Set(existingUrls), [existingUrls]);
  const importCapacity =
    limit !== undefined
      ? Math.max(0, limit - existingUrls.length)
      : Number.MAX_SAFE_INTEGER;

  const { valid: eligibleUrls, invalid, wrongDomain } = useMemo(
    () => partitionUrlsForProjectDomain(fetchedUrls, project),
    [fetchedUrls, project],
  );

  const newEligibleUrls = useMemo(
    () => eligibleUrls.filter((url) => !existingSet.has(url)),
    [eligibleUrls, existingSet],
  );

  const filteredUrls = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return newEligibleUrls;
    return newEligibleUrls.filter((url) => url.toLowerCase().includes(q));
  }, [filter, newEligibleUrls]);

  const selectedCount = selected.size;
  const selectedNew = useMemo(
    () => [...selected].filter((url) => !existingSet.has(url)),
    [selected, existingSet],
  );

  const defaultUrl = useMemo(() => defaultSitemapUrl(project), [
    project.sitemapUrl,
    project.siteUrl,
    project.indexNowDomain,
  ]);

  useEffect(() => {
    if (!show) return;
    setSitemapUrl(defaultUrl);
    setFetchError(null);
    setFetchedUrls([]);
    setFetchedLastmods({});
    setSelected(new Set());
    setFilter('');
  }, [show, defaultUrl]);

  function toggleUrl(url: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(url)) {
        next.delete(url);
        return next;
      }
      if (next.size >= importCapacity) return prev;
      next.add(url);
      return next;
    });
  }

  function selectVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const url of filteredUrls) {
        if (next.size >= importCapacity) break;
        next.add(url);
      }
      return next;
    });
  }

  function clearSelection() {
    setSelected(new Set());
  }

  async function onFetch() {
    setFetchError(null);
    setFetching(true);
    try {
      const data = await fetchSitemapUrls({
        projectId,
        sitemapUrl: sitemapUrl.trim() || undefined,
      }).unwrap();
      setSitemapUrl(data.sitemapUrl);
      setFetchedUrls(data.urls);
      setFetchedLastmods(data.lastmods ?? {});
      setSelected(new Set());
      setFilter('');
    } catch (err) {
      setFetchError(formatSitemapFetchError(err));
      setFetchedUrls([]);
      setFetchedLastmods({});
      setSelected(new Set());
    } finally {
      setFetching(false);
    }
  }

  function onConfirmImport() {
    if (selectedNew.length === 0) return;
    const lastmods: Record<string, string> = {};
    for (const url of selectedNew) {
      if (fetchedLastmods[url]) lastmods[url] = fetchedLastmods[url];
    }
    onImport(
      selectedNew,
      Object.keys(lastmods).length > 0 ? { lastmods } : undefined,
    );
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      scrollable
      centered
      className="sitemap-import-modal"
    >
      <Modal.Header closeButton>
        <div className="d-flex align-items-center gap-2 me-2">
          <Modal.Title className="fs--1 fw-semibold mb-0">وارد کردن از نقشه سایت</Modal.Title>
          <SitemapImportHelpTip />
        </div>
      </Modal.Header>
      <Modal.Body>
        <Form.Label>آدرس sitemap.xml</Form.Label>
        <div className="d-flex gap-2 mb-3">
          <Form.Control
            className="mono"
            placeholder={projectSitemapPlaceholder(project)}
            value={sitemapUrl}
            onChange={(e) => setSitemapUrl(e.target.value)}
            disabled={fetching}
          />
          <Button
            variant="outline-primary"
            className="sitemap-import-fetch-btn flex-shrink-0"
            disabled={fetching || !sitemapUrl.trim()}
            onClick={() => void onFetch()}
          >
            {fetching ? (
              <Spinner
                size="sm"
                animation="border"
                style={{ width: 12, height: 12 }}
              />
            ) : (
              'واکشی'
            )}
          </Button>
        </div>

        {fetchError && (
          <Alert variant="danger" className="py-2 small mb-0">
            {fetchError}
          </Alert>
        )}

        {fetchedUrls.length > 0 && (
          <>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <p className="text-secondary small mb-0">
                {formatFaNumber(fetchedUrls.length)} آدرس یافت شد
                {limit !== undefined
                  ? existingUrls.length > 0
                    ? ` · ${formatFaNumber(importCapacity)} جای خالی در لیست`
                    : ` · حداکثر ${formatFaNumber(limit)} آدرس`
                  : ''}
              </p>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={filteredUrls.length === 0 || importCapacity === 0}
                  onClick={selectVisible}
                >
                  انتخاب همه
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={selectedCount === 0}
                  onClick={clearSelection}
                >
                  پاک کردن انتخاب
                </Button>
              </div>
            </div>

            {(invalid.length > 0 || wrongDomain.length > 0) && (
              <Alert variant="warning" className="py-2 small">
                {invalid.length > 0 &&
                  `${formatFaNumber(invalid.length)} آدرس نامعتبر نادیده گرفته شد.`}
                {invalid.length > 0 && wrongDomain.length > 0 ? ' ' : ''}
                {wrongDomain.length > 0 &&
                  `${formatFaNumber(wrongDomain.length)} آدرس خارج از دامنه پروژه.`}
              </Alert>
            )}

            {existingSet.size > 0 && newEligibleUrls.length < eligibleUrls.length && (
              <Alert variant="secondary" className="border py-2 small">
                {formatFaNumber(eligibleUrls.length - newEligibleUrls.length)} آدرس
                قبلاً در لیست هستند.
              </Alert>
            )}

            {limit !== undefined && importCapacity === 0 && (
              <Alert variant="warning" className="py-2 small">
                لیست آدرس‌ها پر است (حداکثر {formatFaNumber(limit)}). ابتدا چند
                آدرس حذف کنید.
              </Alert>
            )}

            <Form.Control
              className="mb-2"
              placeholder="جستجو در آدرس‌ها…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              disabled={newEligibleUrls.length === 0}
            />

            <ListGroup
              variant="flush"
              dir="ltr"
              className="border rounded overflow-auto bg-body-tertiary text-body text-start"
              style={{ maxHeight: 'min(360px, 50vh)' }}
            >
              {filteredUrls.length === 0 ? (
                <ListGroup.Item className="bg-transparent text-secondary text-center py-4 border-0" dir="rtl">
                  {newEligibleUrls.length === 0
                    ? 'آدرس جدیدی برای وارد کردن نیست.'
                    : 'نتیجه‌ای برای جستجو نیست.'}
                </ListGroup.Item>
              ) : (
                filteredUrls.map((url) => {
                  const checked = selected.has(url);
                  const disabled =
                    !checked && selectedCount >= importCapacity;
                  return (
                    <ListGroup.Item
                      key={url}
                      className={`bg-transparent text-body py-2 text-start${disabled ? ' opacity-50' : ''}`}
                    >
                      <Form.Check
                        type="checkbox"
                        className="mb-0"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleUrl(url)}
                        label={
                          <span className="font-monospace text-break text-body">
                            {decodeUrlForDisplay(url)}
                          </span>
                        }
                      />
                    </ListGroup.Item>
                  );
                })
              )}
            </ListGroup>

            <p className="text-secondary small mt-2 mb-0">
              {formatFaNumber(selectedCount)} آدرس انتخاب شده
              {limit !== undefined && importCapacity > 0
                ? ` · حداکثر ${formatFaNumber(importCapacity)} مورد قابل وارد کردن`
                : ''}
            </p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          انصراف
        </Button>
        <Button
          variant="falcon-primary"
          disabled={selectedNew.length === 0}
          onClick={onConfirmImport}
        >
          وارد کردن ({formatFaNumber(selectedNew.length)})
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
